/**
 * Suite Service
 * Handles API calls for test suite management (MULTI_SUITE structure)
 */

import { api } from "./apiService";
import { getStoredUser, getStoredClientId } from "../utils/tokenUtils";

export const suiteService = {
  /**
   * Create a new test suite
   */
  createSuite: async (suiteData) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      ...suiteData,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("/suite/create", payload);
    return response.data;
  },

  /**
   * Get all suites for a project
   * Backend returns { success, message, data: { suites: [...], pagination } } — normalize `suites` to top level for callers.
   */
  getSuites: async (project_id, client_id, include_deleted = false) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      include_deleted,
    };
    // api.post() already returns the JSON body (not axios response): { success, message, data: { suites, pagination } }
    const envelope = await api.post("/suite/list", payload) || {};
    const inner = envelope.data;
    const suites = Array.isArray(inner?.suites)
      ? inner.suites
      : Array.isArray(inner)
        ? inner
        : Array.isArray(envelope.suites)
          ? envelope.suites
          : [];
    const pagination =
      inner && typeof inner === "object" && !Array.isArray(inner)
        ? inner.pagination
        : envelope.pagination;

    return {
      ...envelope,
      suites,
      pagination,
    };
  },

  /**
   * Get suite by ID
   */
  getSuiteById: async (suite_id, project_id, client_id) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      suite_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
    };
    const response = await api.post("/suite/get", payload);
    return response.data;
  },

  /**
   * Update a test suite
   */
  updateSuite: async (suite_id, project_id, client_id, updates) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      suite_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      ...updates,
    };
    const response = await api.post("/suite/update", payload);
    return response.data;
  },

  /**
   * Delete a test suite
   */
  deleteSuite: async (suite_id, project_id, client_id) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      suite_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
    };
    const response = await api.post("/suite/delete", payload);
    return response.data;
  },

  /**
   * Clone a test suite (deep copy)
   */
  cloneSuite: async (suite_id, project_id, client_id, new_name) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      suite_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      new_name,
    };
    const response = await api.post("/suite/clone", payload);
    return response.data;
  },
};
